import {
  Button,
  DeleteIcon,
  FileIcon,
  FileUploader,
  Form,
  FormField,
  Input,
  ModalDialog,
  MultiCheckbox,
  Select,
  Textarea,
} from "@rothoblaas/design-system";
import classNames from "classnames";
import { FC, useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import {
  HELP_REQUEST_MAX_ATTACHMENTS,
  HELP_REQUEST_MAX_ATTACHMENTS_MEGABYTES,
  HELP_REQUEST_MAX_ATTACHMENTS_SIZE,
  HELP_REQUEST_MESSAGE_MAX_LENGTH,
  HELP_REQUEST_SUBJECT_MAX_LENGTH,
} from "../../constants/helpRequests";
import NotificationDispatch, { showErrorNotification, showSuccessNotification } from "../../context/notificationContext";
import useI18n from "../../hooks/useTranslations";
import { HelpRequestDiagnostics, SendHelpRequestBody } from "../../http/types/helpRequests";
import { formatFileSize } from "../../utils/fileSize";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Icon from "../ui/Icon";
import styles from "./SupportRequestDialog.module.css";

export type SupportRequestTypeOption = {
  value: string;
  label: string;
};

/** What is captured before the dialog opens. */
export type SupportRequestCapture = {
  // undefined when the capture failed
  screenshot?: Blob;
  diagnostics: HelpRequestDiagnostics;
};

interface SupportRequestDialogForm {
  type?: SupportRequestTypeOption;
  subject: string;
  message: string;
}

interface SupportRequestDialogProps extends SupportRequestCapture {
  texts: {
    title: string;
    intro: string;
    success: string;
  };
  // shows a required type select when set
  typeOptions?: SupportRequestTypeOption[];
  send: (body: SendHelpRequestBody & { type?: string }) => Promise<unknown>;
  onClose: () => void;
}

const SCREENSHOT_OPTION_ID = "screenshot";

/** The form shared by the help requests and the test users' feedback. */
const SupportRequestDialog: FC<SupportRequestDialogProps> = ({
  texts,
  typeOptions,
  send,
  screenshot,
  diagnostics,
  onClose,
}) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);

  const [screenshotUrl, setScreenshotUrl] = useState<string>();
  const [attachScreenshot, setAttachScreenshot] = useState(true);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [attachmentsError, setAttachmentsError] = useState<string>();

  useEffect(() => {
    if (!screenshot) {
      return;
    }
    const url = URL.createObjectURL(screenshot);
    setScreenshotUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [screenshot]);

  const addAttachment = (file: File) => {
    if (attachments.length >= HELP_REQUEST_MAX_ATTACHMENTS) {
      setAttachmentsError(i18n.translation.helpRequests.errors.tooManyAttachments(HELP_REQUEST_MAX_ATTACHMENTS));
      return;
    }
    const totalSize = attachments.reduce((size, attachment) => size + attachment.size, file.size);
    if (totalSize > HELP_REQUEST_MAX_ATTACHMENTS_SIZE) {
      setAttachmentsError(
        i18n.translation.helpRequests.errors.attachmentsTooLarge(HELP_REQUEST_MAX_ATTACHMENTS_MEGABYTES),
      );
      return;
    }
    setAttachmentsError(undefined);
    setAttachments([...attachments, file]);
  };

  const removeAttachment = (index: number) => {
    setAttachmentsError(undefined);
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  return (
    <ModalDialog onClose={onClose} title={texts.title} className={styles.dialog}>
      <Form<SupportRequestDialogForm>
        onSubmit={() => async (formData) =>
          send({
            type: formData.type?.value,
            subject: formData.subject,
            message: formData.message,
            diagnostics,
            screenshot: attachScreenshot ? screenshot : undefined,
            attachments,
          })
            .then(() => {
              dispatch(showSuccessNotification(i18n.translation, texts.success));
              onClose();
            })
            .catch((err) => dispatch(showErrorNotification(i18n, err)))
        }
        submitText={i18n.translation.helpRequests.send}
        secondaryButton={
          <Button onClick={onClose} variant="secondary">
            {i18n.translation.common.cancel}
          </Button>
        }
      >
        {({ register, control, formState: { errors } }) => (
          <FormFieldsContainer fullWidth>
            <p className={styles.intro}>{texts.intro}</p>
            {typeOptions && (
              <Controller
                name="type"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.translation.helpRequests.type} error={fieldState.error}>
                    {({ labelId }) => (
                      <Select
                        {...field}
                        inputId={labelId}
                        options={typeOptions}
                        placeholder={`${i18n.translation.common.select}...`}
                        getOptionValue={(x) => x.value}
                        getOptionLabel={(x) => x.label}
                        isInvalid={fieldState.invalid}
                      />
                    )}
                  </FormField>
                )}
              />
            )}
            <FormField label={i18n.translation.helpRequests.subject} error={errors.subject}>
              {({ labelId, isOptional, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  maxLength={HELP_REQUEST_SUBJECT_MAX_LENGTH}
                  {...register("subject", { required: !isOptional, validate: (value) => !!value.trim() })}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.helpRequests.message} error={errors.message}>
              {({ labelId, isOptional, isInvalid }) => (
                <Textarea
                  id={labelId}
                  rows={6}
                  isInvalid={isInvalid}
                  maxLength={HELP_REQUEST_MESSAGE_MAX_LENGTH}
                  {...register("message", { required: !isOptional, validate: (value) => !!value.trim() })}
                />
              )}
            </FormField>
            <div className={styles.columns}>
              <FormField label={i18n.translation.helpRequests.screenshot} isOptional>
                {() =>
                  screenshotUrl ? (
                    <>
                      <img
                        src={screenshotUrl}
                        alt={i18n.translation.helpRequests.screenshot}
                        className={classNames(styles.screenshot, { [styles.excluded]: !attachScreenshot })}
                      />
                      <MultiCheckbox
                        values={[
                          {
                            id: SCREENSHOT_OPTION_ID,
                            title: i18n.translation.helpRequests.attachScreenshot,
                            selected: attachScreenshot,
                          },
                        ]}
                        onUpdate={(_, checked) => setAttachScreenshot(checked)}
                      />
                    </>
                  ) : (
                    <p className={styles.hint}>{i18n.translation.helpRequests.screenshotUnavailable}</p>
                  )
                }
              </FormField>
              <FormField
                label={i18n.translation.helpRequests.attachments}
                isOptional
                error={attachmentsError ? { type: "validate", message: attachmentsError } : undefined}
              >
                {() => (
                  <>
                    <FileUploader onSelect={addAttachment} locale={i18n.translation.languageCode} />
                    <p className={styles.hint}>
                      {i18n.translation.helpRequests.attachmentsHint(
                        HELP_REQUEST_MAX_ATTACHMENTS,
                        HELP_REQUEST_MAX_ATTACHMENTS_MEGABYTES,
                      )}
                    </p>
                    {attachments.length > 0 && (
                      <ul className={styles.attachments}>
                        {attachments.map((file, index) => (
                          <li key={`${file.name}-${index}`} className={styles.attachment}>
                            <FileIcon fileName={file.name} />
                            <span className={styles.attachmentName}>{file.name}</span>
                            <span className={styles.attachmentSize}>{formatFileSize(file.size)}</span>
                            <Button
                              variant="tertiary"
                              className={styles.removeAttachment}
                              aria-label={i18n.translation.helpRequests.removeAttachment}
                              title={i18n.translation.helpRequests.removeAttachment}
                              onClick={() => removeAttachment(index)}
                            >
                              <Icon glyph={DeleteIcon} />
                            </Button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </FormField>
            </div>
            <p className={styles.hint}>{i18n.translation.helpRequests.diagnosticsInfo}</p>
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default SupportRequestDialog;
